import styled from '@emotion/styled';
import Icon from '../Icon';
import { breakpoints, colors, spacing } from '../../../utils/styleguide';
import Typography, { TypographyVariants } from '../text/Typography';
import { css } from '@emotion/react';
import Divider from '../Divider';
import { Locked } from '@carbon/icons-react';

const PaymentFooterContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: ${spacing[4]}px ${spacing[3]}px;
  align-items: flex-start;
  gap: ${spacing[1]}px;
  align-self: stretch;
  background: ${colors.grey01};
  border: 0.5px solid ${colors.grey03};
  border-top: none;
  border-radius: 0 0 4px 4px;

  ${breakpoints.desktop} {
    padding: ${spacing[5]}px ${spacing[4]}px;
  }
`;

const SecurePayments = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[1]}px;
  padding-bottom: ${spacing[1]}px;

  svg,
  p {
    padding-right: ${spacing[1]}px;
  }
`;

const SecurePaymentsTitle = styled.div`
  display: flex;
  gap: ${spacing[0]}px;
`;

const SecurePaymentsIcons = styled.div`
  display: flex;
  gap: ${spacing[2]}px;
`;

const DonationAmountClarification = styled.div`
  padding: 0 0 ${spacing[3]}px 0;
`;

const PaymentFooter = ({
  donationClarification,
  securePaymentsLabel,
  termsAndPrivacyCopyright: { donationTerms, terms, and, privacy },
}: {
  donationClarification?: string;
  securePaymentsLabel?: string;
  termsAndPrivacyCopyright: Record<string, string>;
}) => {
  return (
    <PaymentFooterContainer>
      {donationClarification && (
        <DonationAmountClarification>
          <Typography variant="bodySmall" color={colors.blackSecondary}>
            {donationClarification}
          </Typography>
        </DonationAmountClarification>
      )}

      <Divider color={colors.grey03} css={{ width: '100%', margin: `${spacing[1]}px 0` }} />

      <SecurePayments>
        {securePaymentsLabel && (
          <SecurePaymentsTitle>
            <Locked fill={colors.blackDisabled} style={{ padding: 0 }} />
            <Typography variant="bodySmall" color={colors.blackDisabled}>
              {securePaymentsLabel}
            </Typography>
          </SecurePaymentsTitle>
        )}
        <SecurePaymentsIcons>
          <Icon size={24} fill={colors.blackDisabled} style={{ padding: 0 }} name="visa-solid" />
          <Icon
            size={24}
            fill={colors.blackDisabled}
            style={{ padding: 0 }}
            name="mastercard-solid"
          />
          <Icon
            size={24}
            fill={colors.blackDisabled}
            style={{ padding: 0 }}
            name="american-express-solid"
          />
          <Icon size={24} fill={colors.blackDisabled} style={{ padding: 0 }} name="paypal-solid" />
        </SecurePaymentsIcons>
        <Typography variant="bodySmall" color={colors.blackDisabled}>
          <>
            {`${donationTerms} `}
            <Link href="/terms-of-use">{terms}</Link>
            {` ${and} `}
            <Link href="/privacy-policy">{privacy}</Link>
          </>
        </Typography>
      </SecurePayments>
    </PaymentFooterContainer>
  );
};

type LinkProps = {
  href?: string;
  color?: string;
  variant?: TypographyVariants;
  children?: string;
};

export const Link = ({
  href = '',
  color = colors.blackSecondary,
  variant = 'bodySmall',
  children,
}: LinkProps) => (
  <Typography
    useBuiltInMargins
    variant={variant}
    tag="a"
    href={href}
    css={css`
      color: ${color};

      :hover {
        cursor: pointer;
        color: ${colors.purple400};
      }
    `}
  >
    {children}
  </Typography>
);

export default PaymentFooter;
